import { useState, useEffect } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  Check,
  Trash2,
  Users,
  UserCheck,
  UserX,
  Building2,
  Search,
  Filter,
  MoreVertical,
  Mail,
  Calendar,
  Sparkles,
  User,
} from "lucide-react";
import PROFILE_API from "../../data/api";

export const UsersPanelContent = ({ usersSummary, closePanel }) => {
  const [selectedUser, setSelectedUser] = useState(null);
  const [vendorCount, setVendorCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("all");

  const handleBackgroundClick = (e) => {
    if (e.target === e.currentTarget) closePanel();
  };

  useEffect(() => {
    const fetchVendors = async () => {
      try {
        const response = await axios.get(PROFILE_API.GET_ALL);
        const vendors = response.data.filter((u) => u.type === "Vendor");
        setVendorCount(vendors.length);
      } catch (error) {
        console.error("Error fetching vendors:", error);
      }
    };
    fetchVendors();
  }, []);

  // Filter users
  const filteredUsers = (usersSummary?.recent || []).filter((user) => {
    const matchesSearch =
      user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType =
      filterType === "all" || user.role?.toLowerCase() === filterType.toLowerCase();
    return matchesSearch && matchesType;
  });

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex justify-center items-start overflow-y-auto py-8"
      onClick={handleBackgroundClick}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        className="w-full max-w-6xl bg-white rounded-3xl shadow-2xl border border-gray-200 p-8 mx-4 relative"
      >
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-between mb-8"
        >
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-indigo-600 to-purple-600 flex items-center justify-center shadow-lg shadow-indigo-500/30">
              <Users className="w-8 h-8 text-white" />
            </div>
            <div>
              <h2 className="text-3xl font-extrabold text-gray-900 tracking-tight">
                Users Overview
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                Quick summary & recent activity
              </p>
            </div>
          </div>
          <button
            onClick={closePanel}
            className="w-10 h-10 flex items-center justify-center rounded-xl bg-gray-100 hover:bg-gray-200 transition-colors"
          >
            <X size={20} className="text-gray-600" />
          </button>
        </motion.div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          {[
            {
              label: "Total Users",
              value: usersSummary?.users ?? 0,
              icon: Users,
              gradient: "from-purple-500 to-purple-600",
            },
            {
              label: "Active Users",
              value: usersSummary?.active ?? 0,
              icon: UserCheck,
              gradient: "from-green-500 to-emerald-500",
            },
            {
              label: "Blocked Users",
              value: usersSummary?.blocked ?? 0,
              icon: UserX,
              gradient: "from-red-500 to-rose-500",
            },
            {
              label: "Total Vendors",
              value: vendorCount,
              icon: Building2,
              gradient: "from-yellow-500 to-amber-500",
            },
            {
              label: "New This Week",
              value: 12,
              icon: Sparkles,
              gradient: "from-blue-500 to-cyan-500",
            },
          ].map((stat, i) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              whileHover={{ scale: 1.02, y: -4 }}
              className="bg-white rounded-2xl p-5 shadow-lg border border-gray-100 cursor-pointer"
            >
              <div className="flex items-center justify-between mb-3">
                <div
                  className={`w-10 h-10 rounded-xl bg-gradient-to-br ${stat.gradient} flex items-center justify-center`}
                >
                  <stat.icon className="w-5 h-5 text-white" />
                </div>
                <Sparkles className="w-4 h-4 text-gray-300" />
              </div>
              <p className="text-xs font-medium text-gray-500">{stat.label}</p>
              <h3 className="text-2xl font-bold text-gray-800 mt-1">
                {stat.value.toLocaleString()}
              </h3>
            </motion.div>
          ))}
        </div>

        {/* Search and Filter */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search
              className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
              size={20}
            />
            <input
              type="text"
              placeholder="Search users by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-white border border-gray-200 rounded-xl focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 outline-none transition-all"
            />
          </div>
          <div className="relative">
            <Filter
              className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
              size={20}
            />
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="pl-12 pr-8 py-3 bg-white border border-gray-200 rounded-xl focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 outline-none transition-all appearance-none cursor-pointer"
            >
              <option value="all">All Roles</option>
              <option value="user">Users</option>
              <option value="seller">Sellers</option>
              <option value="vendor">Vendors</option>
            </select>
          </div>
        </div>

        {/* Recent Users */}
        <div className="mb-6">
          <h4 className="text-gray-800 font-semibold mb-4 text-lg flex items-center gap-2">
            <Calendar size={20} className="text-indigo-600" />
            Recent Registrations
          </h4>
          <div className="space-y-3 max-h-80 overflow-y-auto pr-2">
            <AnimatePresence>
              {filteredUsers.length === 0 ? (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-center py-12"
                >
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
                    <User size={32} className="text-gray-400" />
                  </div>
                  <p className="text-gray-500">No users found</p>
                </motion.div>
              ) : (
                filteredUsers.map((u, index) => (
                  <motion.div
                    key={u.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    transition={{ delay: index * 0.05 }}
                    whileHover={{ scale: 1.01, x: 4 }}
                    className="flex items-center gap-4 p-4 rounded-xl border border-gray-100 hover:shadow-lg transition-all cursor-pointer bg-white"
                    onClick={() => setSelectedUser(u)}
                  >
                    <div className="relative">
                      <img
                        src={`https://i.pravatar.cc/40?img=${u.id}`}
                        alt={u.name}
                        className="w-12 h-12 rounded-full border-2 border-gray-200"
                      />
                      <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-gray-800 truncate">{u.name}</p>
                      <p className="text-sm text-gray-500 truncate flex items-center gap-1">
                        <Mail size={12} />
                        {u.email}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span
                        className={`text-xs px-3 py-1 rounded-full font-medium ${
                          u.role === "Seller"
                            ? "bg-purple-100 text-purple-700"
                            : u.role === "Vendor"
                            ? "bg-yellow-100 text-yellow-700"
                            : "bg-indigo-100 text-indigo-700"
                        }`}
                      >
                        {u.role}
                      </span>
                      <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                        <MoreVertical size={16} className="text-gray-400" />
                      </button>
                    </div>
                  </motion.div>
                ))
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Close Button */}
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={closePanel}
          className="w-full py-3 rounded-xl border border-gray-200 hover:bg-gray-50 font-medium transition-colors"
        >
          Close Panel
        </motion.button>

        {/* User Detail Modal */}
        <AnimatePresence>
          {selectedUser && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-60 bg-black/50 backdrop-blur-sm flex items-center justify-center px-4"
              onClick={(e) => e.target === e.currentTarget && setSelectedUser(null)}
            >
              <motion.div
                initial={{ scale: 0.95, opacity: 0, y: 20 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                exit={{ scale: 0.95, opacity: 0, y: 20 }}
                className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
              >
                {/* Modal Header */}
                <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-6 text-white">
                  <div className="flex items-center justify-between">
                    <h3 className="text-xl font-bold">User Details</h3>
                    <button
                      onClick={() => setSelectedUser(null)}
                      className="w-8 h-8 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 transition-colors"
                    >
                      <X size={16} />
                    </button>
                  </div>
                </div>

                {/* User Info */}
                <div className="p-6">
                  <div className="flex items-center gap-4 mb-6">
                    <img
                      src={`https://i.pravatar.cc/80?img=${selectedUser.id}`}
                      alt={selectedUser.name}
                      className="w-20 h-20 rounded-full border-4 border-gray-100"
                    />
                    <div>
                      <h4 className="text-xl font-bold text-gray-800">
                        {selectedUser.name}
                      </h4>
                      <p className="text-gray-500 flex items-center gap-1">
                        <Mail size={14} />
                        {selectedUser.email}
                      </p>
                      <span
                        className={`inline-flex items-center gap-1 mt-2 px-3 py-1 rounded-full text-xs font-medium ${
                          selectedUser.role === "Seller"
                            ? "bg-purple-100 text-purple-700"
                            : selectedUser.role === "Vendor"
                            ? "bg-yellow-100 text-yellow-700"
                            : "bg-indigo-100 text-indigo-700"
                        }`}
                      >
                        {selectedUser.role}
                      </span>
                    </div>
                  </div>

                  {/* User Stats */}
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="bg-gray-50 rounded-xl p-4">
                      <p className="text-xs text-gray-500">Orders</p>
                      <p className="text-lg font-bold text-gray-800">24</p>
                    </div>
                    <div className="bg-gray-50 rounded-xl p-4">
                      <p className="text-xs text-gray-500">Spent</p>
                      <p className="text-lg font-bold text-gray-800">₹12,450</p>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-3">
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="flex-1 py-2.5 rounded-xl bg-gradient-to-r from-green-500 to-emerald-500 text-white flex items-center justify-center gap-2 shadow-lg shadow-green-500/30"
                    >
                      <Check size={16} /> Verify
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="flex-1 py-2.5 rounded-xl bg-gradient-to-r from-red-500 to-rose-500 text-white flex items-center justify-center gap-2 shadow-lg shadow-red-500/30"
                    >
                      <Trash2 size={16} /> Delete
                    </motion.button>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
};

export default UsersPanelContent;
